import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { timeInTz } from '../../lib/format';
import { StatusBadge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import {
  ErrorState,
  ForbiddenState,
  OfflineState,
  isForbidden,
  shownState,
  StaleNotice,
  refreshFailed,
} from '../ui/QueryStates';
import { Skeleton } from '../ui/Skeleton';
import { VISIT_STATUS, display } from '../status';
import { dayTabs, rowsForTab, isDayTab, ALL_TAB } from './dayTabs';
import { pickNextStop } from './nextStop';
import { NextStopCard } from './NextStopCard';
import { UnderlineTabs, UnderlineTabList, UnderlineTab } from '../ui/Tabs';
import { IconSchedule, IconChevronRight, IconInventory } from '../icons';
import { dayRange, todayISO, addDaysISO } from '../../lib/format';
import { WithOrgClock } from '../ui/WithOrgClock';
import { RestockTodayPill } from '../inventory/RestockSheet';

type Row = {
  visit: {
    id: string;
    status: string;
    scheduledStartAt: string | Date | null;
    window: string;
    updatedAt: string | Date;
  };
  job: { id: string; jobNumber: number; title: string; priority: string };
  customer: { displayName: string };
  /* `state` travels in `boardColumns` and was dropped here, so the list built a
     two-part address while the card below wants the one the tech would type into a map. */
  property: { address1: string | null; city: string | null; state: string | null };
};

/*
 * This screen carried its own `dayRange`, `todayISO` and `shiftDay` — three private copies
 * of helpers that already existed in `lib/format`, all reading the BROWSER's clock. On the
 * one surface where that matters most: a tech's phone picks up the local zone wherever it
 * is, so a van working a state line could open a different day's stops than the dispatcher
 * had assigned. The copy of `dayRange` was also inclusive-ended (`T23:59:59.999`) where the
 * server's window is half-open, so the two ends of the wire disagreed about the last
 * millisecond of every day. Use the shared ones.
 */

/**
 * The tech's day — the first screen of the shift and, most days, the only one they
 * think about. One tap per stop, nothing else competing for the thumb.
 */
export function TechTodayPage() {
  return (
    <WithOrgClock what="your day" icon={IconSchedule} title="Today">
      {(tz) => <TechDay tz={tz} />}
    </WithOrgClock>
  );
}

function TechDay({ tz }: { tz: string }) {
  const trpc = useTRPC();
  const [dayIso, setDayIso] = useState(todayISO(tz));
  /**
   * Which status the day is filtered to (119, ref 23411385's list tabs).
   *
   * ⚠️ Local, not in the URL. `useUrlTab` is the office idiom because a colleague pastes a
   * tabbed page's address at somebody; nobody deep-links a tech's own day, and the tech
   * shell is offline-first where a navigation is the one thing that can go wrong. The
   * PRIMITIVE is still shared — `UnderlineTabs`, not a second tab component (119's own
   * "do NOT create a second tab component for the tech shell").
   */
  const [tab, setTab] = useState<string>(ALL_TAB);
  const visits = useQuery(trpc.visit.myDay.queryOptions(dayRange(dayIso, tz)));

  const isToday = dayIso === todayISO(tz);

  const nav = (
    <div className="flex items-center justify-between gap-2 px-4 py-3">
      <button
        type="button"
        onClick={() => setDayIso(addDaysISO(dayIso, -1))}
        className="min-h-11 rounded-[var(--radius)] border border-border px-3 text-sm"
      >
        ←
      </button>
      <div className="text-center">
        <p className="font-heading text-base font-bold">
          {isToday
            ? 'Today'
            : new Date(`${dayIso}T12:00:00`).toLocaleDateString(undefined, {
                weekday: 'long',
              })}
        </p>
        <p className="text-xs text-muted-foreground">
          {new Date(`${dayIso}T12:00:00`).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          })}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setDayIso(addDaysISO(dayIso, 1))}
        className="min-h-11 rounded-[var(--radius)] border border-border px-3 text-sm"
      >
        →
      </button>
    </div>
  );

  // `shownState`: a tech in a van whose refresh fails must not lose the day they are
  // standing in the middle of. This is the surface the whole 042 ruling is for — the
  // stops are on screen, correct, and were about to be replaced by "Couldn't load your
  // day" because a poll failed at a bad moment for the signal.
  const state = shownState(visits, visits.data);
  if (state === 'offline') {
    return (
      <div>
        {nav}
        <RestockTodayPill />
        <div className="p-4">
          <OfflineState icon={IconSchedule} what="this day" />
        </div>
      </div>
    );
  }
  if (state === 'loading') {
    return (
      <div>
        {nav}
        <RestockTodayPill />
        <div className="space-y-3 p-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div>
        {nav}
        <RestockTodayPill />
        <div className="p-4">
          {/* `visit.myDay` is gated on `visit:read` now, and nothing guards the /tech route
              itself — no route in this app has a `beforeLoad`. So a role without that
              permission lands here, and before this branch existed they got "Something went
              wrong… Try again" under a button that could only ever fail. A 403 is a
              permanent answer about the account, not a bad day for the server. */}
          {isForbidden(visits.error) ? (
            <ForbiddenState icon={IconSchedule} what="the field schedule" />
          ) : (
            <ErrorState
              icon={IconSchedule}
              what="your day"
              onRetry={() => void visits.refetch()}
            />
          )}
        </div>
      </div>
    );
  }

  const rows = (visits.data as Row[]).slice().sort((a, b) => {
    const ta = a.visit.scheduledStartAt
      ? new Date(a.visit.scheduledStartAt).getTime()
      : 0;
    const tb = b.visit.scheduledStartAt
      ? new Date(b.visit.scheduledStartAt).getTime()
      : 0;
    return ta - tb;
  });
  const done = rows.filter((r) => r.visit.status === 'completed').length;
  const next = pickNextStop(rows, (r) => r.visit);
  const tabs = dayTabs(rows);
  // Junk falls back to All rather than showing an empty day — a filter nobody chose is
  // indistinguishable on screen from having no work.
  const activeTab = isDayTab(tab) ? tab : ALL_TAB;
  const shown = rowsForTab(rows, activeTab);

  return (
    <div>
      {nav}
      <RestockTodayPill />
      {/* 🔴 The tech pages were converted to `shownState` and given no notice, so a tech
          could work an outdated route with nothing on screen saying the refresh had failed.
          The guard missed it because its population was "files mounting UnresolvedState",
          and these branch through ErrorState directly — a guard over a component family
          rather than over the rule. (Codex adversary, round 3.) */}
      <div className="px-4 pt-3">
        <StaleNotice
          show={refreshFailed(visits, visits.data)}
          onRetry={() => void visits.refetch()}
        />
      </div>
      {/*
        The next-stop moment (117), ABOVE the list and above the van.

        ⚠️ **Today only.** A countdown on Thursday's page would be counting to a stop two
        days out — a number that is technically correct, useless, and reads at a glance
        exactly like the one that matters. `isToday` is already the shop's day, from
        `todayISO(tz)`, so the card inherits the org clock rather than asking again.

        ⚠️ Rendered from `rows`, the SORTED and UNFILTERED day — never from `shown`. The
        status strip is a filter on the list, and the next stop is a fact about the day: a
        tech reading the Completed tab must not be told their next stop is a finished one,
        and one reading Canceled must not be told they have none.

        ⚠️ Keyed by the stop, so a new next stop gets a new card. Unkeyed, the card's writes
        (`useMutation` observers) outlived their stop, and a write still pending for the last
        one shut and relabelled this one's controls.
      */}
      {isToday && next ? (
        <NextStopCard
          key={next.row.visit.id}
          row={next.row}
          startsAt={next.startsAt}
          tz={tz}
        />
      ) : null}
      {/* The van sits under the day, not in a menu: it's the second thing a tech opens,
          and it's how they answer "do I have one on board" without calling the office. */}
      <div className="px-4 pb-3">
        <Link
          to="/tech/van"
          className="flex min-h-11 items-center gap-2 rounded-[var(--radius)] border border-border bg-surface px-3 text-sm font-medium"
        >
          <IconInventory size={16} className="text-muted-foreground" />
          What's on my van
          <IconChevronRight size={15} className="ml-auto text-muted-foreground" />
        </Link>
      </div>
      {rows.length === 0 ? (
        <div className="p-4">
          <EmptyState
            icon={IconSchedule}
            title={isToday ? 'Nothing booked today' : 'Nothing booked'}
            description="When dispatch assigns you a stop, it shows up here."
          />
        </div>
      ) : (
        <>
          <p className="px-4 pb-2 text-xs text-muted-foreground">
            {done} of {rows.length} done
          </p>
          {/*
            The status strip (ref 23411385). `UnderlineTabList` already scrolls horizontally
            at 390px (`overflow-x-auto` + `min-w-max`) — A-2's sanctioned answer to density,
            and what the reference's own strip does (its last tab is visibly cut off).

            ⚠️ Rendered OUTSIDE `UnderlineTabPanel`: the list below is one filtered `<ul>`,
            not six mounted panels. Six panels would mount six copies of the day, and Radix
            would unmount five of them on every tap — six times the work to show one list.
          */}
          <div className="px-4 pb-3">
            <UnderlineTabs value={activeTab} onValueChange={setTab}>
              <UnderlineTabList>
                {tabs.map((t) => (
                  <UnderlineTab key={t.key} value={t.key}>
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      {t.label}
                      <span className="tabular-nums text-muted-foreground">
                        {t.count}
                      </span>
                    </span>
                  </UnderlineTab>
                ))}
              </UnderlineTabList>
            </UnderlineTabs>
          </div>
          {shown.length === 0 ? (
            <div className="px-4 pb-6">
              {/* An empty TAB is not an empty day, and saying so is the difference between
                  "you have nothing to do" and "nothing here matches this filter". */}
              <EmptyState
                icon={IconSchedule}
                title={`Nothing ${tabs.find((t) => t.key === activeTab)?.label.toLowerCase() ?? ''}`}
                description="Other stops on this day are under the other tabs."
              />
            </div>
          ) : (
            <ul className="space-y-2 px-4 pb-6">
              {shown.map((r) => {
                const s = display(VISIT_STATUS, r.visit.status);
                const finished = r.visit.status === 'completed';
                return (
                  <li key={r.visit.id}>
                    <Link
                      to="/tech/visits/$visitId"
                      params={{ visitId: r.visit.id }}
                      className={`flex min-h-[76px] items-center gap-3 rounded-[var(--radius)] border border-border bg-surface p-3 ${
                        finished ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="w-16 shrink-0 text-center">
                        <p className="font-mono text-sm font-semibold">
                          {r.visit.scheduledStartAt
                            ? timeInTz(r.visit.scheduledStartAt, tz)
                            : '—'}
                        </p>
                        {r.job.priority === 'urgent' ? (
                          <StatusBadge tone="danger">Urgent</StatusBadge>
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{r.customer.displayName}</p>
                        <p className="truncate text-sm text-muted-foreground">
                          {r.job.title}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {[r.property.address1, r.property.city]
                            .filter(Boolean)
                            .join(', ') || '—'}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
                        <IconChevronRight size={16} className="text-muted-foreground" />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
